import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { makeApiCall } from '../utils/customerUtils'
import { Partner, PartnerForm } from './PartnerForm'

interface PartnerSearchDropdownProps {
  selectedPartnerId?: number | null
  onSelect: (partner: Partner | null) => void
  placeholder?: string
  className?: string
}

export function PartnerSearchDropdown({ 
  selectedPartnerId, 
  onSelect, 
  placeholder,
  className = ''
}: PartnerSearchDropdownProps) {
  const { t } = useTranslation()
  const [partners, setPartners] = useState<Partner[]>([])
  const [filteredPartners, setFilteredPartners] = useState<Partner[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null)
  const [loading, setLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetchPartners()
  }, [])

  useEffect(() => {
    // Find selected partner from the list
    if (selectedPartnerId && partners.length > 0) {
      const partner = partners.find(p => p.id === selectedPartnerId)
      if (partner) {
        setSelectedPartner(partner)
        setSearchTerm(partner.name)
      }
    } else if (!selectedPartnerId) {
      setSelectedPartner(null)
      setSearchTerm('')
    }
  }, [selectedPartnerId, partners])

  useEffect(() => {
    // Filter partners based on search term
    if (!searchTerm.trim()) {
      setFilteredPartners(partners)
    } else {
      const search = searchTerm.toLowerCase()
      const filtered = partners.filter(partner => 
        partner.name.toLowerCase().includes(search) ||
        partner.first_name.toLowerCase().includes(search) ||
        partner.last_name.toLowerCase().includes(search) ||
        partner.phone?.toLowerCase().includes(search) ||
        partner.agency?.toLowerCase().includes(search)
      )
      setFilteredPartners(filtered)
    }
  }, [searchTerm, partners])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchPartners = async () => {
    try {
      setLoading(true)
      const response = await makeApiCall('api/partners')
      if (response.ok) {
        const data = await response.json()
        setPartners(data.partners || [])
      }
    } catch (error) {
      console.error('Error fetching partners:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSelect = (partner: Partner | null) => {
    setSelectedPartner(partner)
    setSearchTerm(partner ? partner.name : '')
    setIsOpen(false)
    onSelect(partner)
  }

  const handleInputChange = (value: string) => {
    setSearchTerm(value)
    setIsOpen(true)
    if (!value && selectedPartner) {
      handleSelect(null)
    }
  }

  const handleCreatePartner = (newPartner: Partner) => {
    // Add to local list
    setPartners(prev => [newPartner, ...prev])
    // Select it
    handleSelect(newPartner)
    setShowCreateModal(false)
  }

  const handleClearSelection = () => {
    handleSelect(null)
  }

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder || t('partners.form.enterFirstName')}
            className={`w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:text-white ${className}`}
          />
          {selectedPartner && (
            <button
              type="button"
              onClick={handleClearSelection}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Clear selection"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
            {loading ? (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                Loading partners...
              </div>
            ) : filteredPartners.length === 0 ? (
              <div className="px-4 py-3">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  No partners found
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(true)
                    setIsOpen(false)
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create new partner
                </button>
              </div>
            ) : (
              <>
                {filteredPartners.map((partner) => (
                  <button
                    key={partner.id}
                    type="button"
                    onClick={() => handleSelect(partner)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:outline-none"
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {partner.name}
                    </div>
                    {(partner.agency || partner.phone) && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {partner.agency && <span>{partner.agency}</span>}
                        {partner.agency && partner.phone && <span className="mx-1">•</span>}
                        {partner.phone && <span>{partner.phone}</span>}
                      </div>
                    )}
                  </button>
                ))}
                <div className="border-t border-gray-200 dark:border-gray-600">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(true)
                      setIsOpen(false)
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create new partner
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Create Partner Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
              onClick={() => setShowCreateModal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {t('partners.addPartner')}
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <PartnerForm
                  onSubmit={handleCreatePartner}
                  onCancel={() => setShowCreateModal(false)}
                  isModal={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}



